import {Card} from "../../../basic/card.tsx";
import {Button} from "../../../basic/button.tsx";
import {Badge} from "../../../basic/badge.tsx";
import {ArrowRight} from "lucide-react";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {VwpLicense} from "../../../../store/generated/api.ts";
import {getLicenseDisplayStatus} from "../../../../utils/licenseUtils.ts";
import {LicenseStatusChip} from "../../../shared/status/LicenseStatusChip";
import {LicenseQuotas} from "../LicensesView/LicenseQuotas";
import {QUOTA_WARNING_THRESHOLD} from "../../../../hooks/useQuotaWarning";

interface LicenseItem {
    name: string;
    count: number;
    status: 'active' | 'expiring' | 'disabled' | 'expired';
    supportLevel?: 'NONE' | 'SMA' | 'SMA_PLUS';
    license: VwpLicense;
    licenses: VwpLicense[];
}

interface LicenseSummaryProps {
    licenses: VwpLicense[];
    onViewAllClick: () => void;
}

export function LicenseSummary({licenses, onViewAllClick}: LicenseSummaryProps) {
    const {t} = useTranslationWithKeys();

    const transformedLicenses: LicenseItem[] = licenses.reduce((acc: LicenseItem[], license) => {
        const displayStatus = getLicenseDisplayStatus(license);
        const existingItem = acc.find(item => item.name === license.product);

        if (existingItem) {
            // If any license for this product is expiring, mark the whole product as expiring
            if (displayStatus === 'expiring' && existingItem.status === 'active') {
                existingItem.status = 'expiring';
            }
            // If any license for this product has support, mark the whole product as having support
            if (license.supportLevel && license.supportLevel !== 'NONE' && !existingItem.supportLevel) {
                existingItem.supportLevel = license.supportLevel;
            }
            existingItem.count++;
            existingItem.licenses.push(license);
        } else {
            acc.push({
                name: license.product,
                count: 1,
                status: displayStatus,
                supportLevel: license.supportLevel,
                license: license,
                licenses: [license]
            });
        }

        return acc;
    }, []);

    return (
        <Card className="p-6 shadow-sm border-border/50">
            <div className="flex items-center justify-between mb-4">
                <h3 className="mb-0">{t('dashboard.licenseSummary')}</h3>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onViewAllClick}
                >
                    {t('dashboard.viewAll')}
                    <ArrowRight className="h-4 w-4 ml-2"/>
                </Button>
            </div>
            <div className="space-y-3">
                {transformedLicenses.map((license, index) => (
                    <div key={index} className="rounded-lg border bg-card">
                        <div className="flex items-center justify-between p-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span>{license.name}</span>
                                    {license.supportLevel === 'SMA' && (
                                        <Badge variant="secondary" className="text-xs">
                                            {t('licenses.sma')}
                                        </Badge>
                                    )}
                                    {license.supportLevel === 'SMA_PLUS' && (
                                        <Badge variant="secondary" className="text-xs">
                                            {t('licenses.smaPlus')}
                                        </Badge>
                                    )}
                                </div>
                                <p className="text-muted-foreground">{t('dashboard.licenseCount', {count: license.count})}</p>
                            </div>
                            <LicenseStatusChip license={license.license}/>
                        </div>
                        {license.licenses.map(item => (
                            <LicenseQuotas
                                key={item.id}
                                license={item}
                                minPercentageUsed={QUOTA_WARNING_THRESHOLD}
                                className="px-3 pt-0 pb-3"
                            />
                        ))}
                    </div>
                ))}
            </div>
        </Card>
    );
}